import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Wraps the bundle quantity selector section. All pricing/discount math
 * and every "includes" list is pre-rendered server-side, once per
 * configured quantity, as a stack of panels (see the section's Liquid) —
 * this component's job is picking which panel is active and keeping the
 * couple of pieces of text that live outside those panels (the
 * stepper's "N Downpipes" label, the shared add-to-cart button's price)
 * in sync with the quantity.
 *
 * One exception: past the last configured Quantity block, there's no
 * server-rendered panel to fall back on (a merchant can't be asked to
 * configure infinite tiers), so that last panel's title/price/discount
 * pill get overwritten here with a computed price instead — the one
 * place this component does its own money math, because there's no
 * alternative. Everything else in that panel (the "Includes" list,
 * badge, totals) is left exactly as server-rendered, per spec: past the
 * last tier, only the quantity and price change.
 *
 * The actual quantity input/stepper and the add-to-cart request itself
 * are the theme's own `quantity-selector-component` and
 * `product-form-component` — this component only listens for the
 * former's `ThemeEvents.quantitySelectorUpdate` (which bubbles) rather
 * than reimplementing increment/decrement or cart submission. There is
 * deliberately no cart-sync logic here (no listening for cart changes
 * made elsewhere, no writing to the cart outside the normal add-to-cart
 * button) — this component only ever affects what happens when the
 * button is actually clicked, same as any other product form.
 *
 * Deliberately does not use the `Component` base's `refs` convenience:
 * `<product-form-component>` sits between this element and most of the
 * elements it needs (it has to, so the add-to-cart button and quantity
 * input are real descendants of the add-to-cart `<form>`), and refs are
 * claimed by the *nearest* Component ancestor — so those refs would
 * belong to `<product-form-component>`, not here. Plain `querySelector`
 * calls sidestep that ownership boundary; it just walks the real DOM.
 *
 * @extends {Component}
 */
class BundleQuantitySelectorComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.tierCount = Number(this.dataset.tierCount) || 0;
    this.unitLabel = this.dataset.unitLabel || '';
    this.unitLabelSingular = this.dataset.unitLabelSingular || this.unitLabel;
    // Separate from unitLabel on purpose — the stepper label and the
    // card title are allowed to use different words (see the section).
    this.cardUnitLabel = this.dataset.cardUnitLabel || this.unitLabel;
    this.basePriceCents = Number(this.dataset.basePrice) || 0;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';
    this.ctaLabelBase = this.dataset.ctaLabelBase || '';
    this.overflowDiscountType = this.dataset.overflowDiscountType || 'none';
    this.overflowDiscountValue = Number(this.dataset.overflowDiscountValue) || 0;
    // The worth of every non-repeatable unlocked-by-tierCount item —
    // repeatable items are excluded here on purpose (see the section)
    // and added back in dynamically in #applyOverflowPricing instead,
    // since their contribution keeps growing past tierCount too.
    this.overflowFixedBonusValueCents = Number(this.dataset.overflowFixedBonusValue) || 0;
    this.totalValueLabel = this.dataset.totalValueLabel || '';
    this.savingsLabel = this.dataset.savingsLabel || '';
    // Independent from tierCount on purpose (see the section settings) —
    // pricing/inclusions past tierCount reuse the last tier, but the
    // Wholesale note can hold off until a separate, later quantity.
    this.wholesaleThreshold = Number(this.dataset.wholesaleThreshold) || this.tierCount;

    this.mediaContainer = this.querySelector('[ref="media"]');
    this.cardsContainer = this.querySelector('[ref="cards"]');
    this.badgesContainer = this.querySelector('[ref="badges"]');
    this.stepperLabel = this.querySelector('[ref="stepperLabel"]');
    this.wholesaleNote = this.querySelector('[ref="wholesaleNote"]');
    this.addToCartButton = this.querySelector('[ref="ctaButton"]');
    this.ctaLabel = this.querySelector('[ref="ctaLabel"]');
    this.trust = this.querySelector('[ref="trust"]');

    // Only present on the last (highest-quantity) card panel — see the
    // markup comment above them.
    this.overflowTitle = this.querySelector('[ref="overflowTitle"]');
    this.overflowPrice = this.querySelector('[ref="overflowPrice"]');
    this.overflowUnitPrice = this.querySelector('[ref="overflowUnitPrice"]');
    this.overflowDiscountPill = this.querySelector('[ref="overflowDiscountPill"]');
    this.overflowTotals = this.querySelector('[ref="overflowTotals"]');
    this.overflowTotalsValue = this.querySelector('[ref="overflowTotalsValue"]');
    this.overflowTotalsSavings = this.querySelector('[ref="overflowTotalsSavings"]');

    // Repeatable items' multiplier badges ("×2", "×3"...) and their
    // struck-through price (which scales by the same multiplier — a
    // ×2'd item shows 2 units' worth, not 1) live inside the last panel
    // too, marked with data-unlock-quantity / data-unit-price-cents —
    // see the markup — and need the same recompute-past-tierCount
    // treatment as the price fields above. Queried separately (distinct
    // classes) even though both carry data-unlock-quantity, since they
    // need different updates (× text vs. a money amount).
    const lastPanel = this.overflowTitle?.closest('.bundle-qty__card');
    this.multiplierBadges = Array.from(lastPanel?.querySelectorAll('.bundle-qty__multiplier[data-unlock-quantity]') ?? []).filter(
      (el) => el instanceof HTMLElement
    );
    this.repeatablePriceEls = Array.from(lastPanel?.querySelectorAll('[data-unit-price-cents]') ?? []).filter(
      (el) => el instanceof HTMLElement
    );

    // Cached so quantities back at/under tier_count can restore the
    // real server-rendered values instead of this component having to
    // re-derive them.
    this.lastTierOriginal = {
      title: this.overflowTitle?.textContent ?? '',
      price: this.overflowPrice?.textContent ?? '',
      unitPrice: this.overflowUnitPrice?.textContent ?? '',
      pillText: this.overflowDiscountPill?.textContent ?? '',
      pillHidden: this.overflowDiscountPill?.hidden ?? true,
      totalsHidden: this.overflowTotals?.hidden ?? true,
      totalsValueText: this.overflowTotalsValue?.textContent ?? '',
      totalsSavingsText: this.overflowTotalsSavings?.textContent ?? '',
      multipliers: this.multiplierBadges.map((el) => ({ text: el.textContent ?? '', hidden: el.hidden })),
      repeatablePrices: this.repeatablePriceEls.map((el) => ({ text: el.textContent ?? '', hidden: el.hidden })),
    };

    this.ctaLabels = this.#parseJsonRef('ctaLabels');
    /** @type {{unlockQuantity: number, priceCents: number}[]} */
    this.repeatableItems = this.#parseJsonRef('repeatableItems');

    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);

    this.#update(this.#getCurrentQuantity());
  }

  /**
   * @param {string} refName
   * @returns {string[]}
   */
  #parseJsonRef(refName) {
    const script = this.querySelector(`[ref="${refName}"]`);
    try {
      return JSON.parse(script?.textContent || '[]');
    } catch {
      return [];
    }
  }

  /** @returns {number} */
  #getCurrentQuantity() {
    const input = this.querySelector('.bundle-qty__stepper input[name="quantity"]');
    return Number(input?.value) || 1;
  }

  /** @param {Event} event */
  #onQuantityChange = (event) => {
    const detail = /** @type {any} */ (event).detail;
    this.#update(detail?.quantity ?? this.#getCurrentQuantity());
  };

  /** @param {number} quantity */
  #update(quantity) {
    const qty = Math.max(1, Math.round(Number(quantity) || 1));
    // Two independent thresholds: past tierCount there's no more
    // server-rendered pricing/inclusions data, so the last panel's
    // price gets computed instead (reusing its discount rule and
    // Includes content). Past wholesaleThreshold (which can sit higher
    // than tierCount) the Wholesale note additionally appears — it
    // doesn't replace the card, both can be true at once.
    const overTierLimit = this.tierCount > 0 && qty > this.tierCount;
    const overWholesaleLimit = this.wholesaleThreshold > 0 && qty > this.wholesaleThreshold;
    const activeIndex = this.tierCount > 0 ? Math.min(qty, this.tierCount) : qty;

    if (this.mediaContainer) this.#setActivePanel(this.mediaContainer, activeIndex);
    if (this.cardsContainer) this.#setActivePanel(this.cardsContainer, activeIndex);
    if (this.badgesContainer) this.#setActivePanel(this.badgesContainer, activeIndex);

    if (this.stepperLabel) {
      const unitWord = qty === 1 ? this.unitLabelSingular : this.unitLabel;
      this.stepperLabel.textContent = `${qty} ${unitWord}`.trim();
    }

    if (this.wholesaleNote) this.wholesaleNote.hidden = !overWholesaleLimit;

    if (overTierLimit) {
      this.#applyOverflowPricing(qty);
    } else {
      this.#restoreLastTierOriginal();
      if (this.ctaLabel) {
        const label = this.ctaLabels[activeIndex - 1];
        if (label) this.ctaLabel.textContent = label;
      }
    }
  }

  /**
   * Past the last configured Quantity block: same discount rule as that
   * block, applied to whatever quantity is actually chosen. The
   * Includes list/badge are left as server-rendered, but total bundle
   * value/savings still have to scale with quantity (they're quantity ×
   * full price + the fixed inclusions value, not just the inclusions),
   * so those get recomputed here too.
   * @param {number} qty
   */
  #applyOverflowPricing(qty) {
    const subtotalCents = this.basePriceCents * qty;
    let bundleCents = subtotalCents;

    if (this.overflowDiscountType === 'percentage' && this.overflowDiscountValue > 0) {
      const keepPct = Math.max(0, 100 - Math.min(this.overflowDiscountValue, 100));
      bundleCents = Math.round((subtotalCents * keepPct) / 100);
    } else if (this.overflowDiscountType === 'fixed' && this.overflowDiscountValue > 0) {
      bundleCents = Math.max(0, subtotalCents - Math.round(this.overflowDiscountValue * 100));
    }

    const unitCents = qty > 0 ? Math.round(bundleCents / qty) : 0;
    const discountPercent =
      subtotalCents > 0 && bundleCents < subtotalCents ? Math.round((1 - bundleCents / subtotalCents) * 100) : 0;

    if (this.overflowTitle) this.overflowTitle.textContent = `${qty}x ${this.cardUnitLabel}`;
    if (this.overflowPrice) this.overflowPrice.textContent = formatMoney(bundleCents, this.moneyFormat, this.currency);
    if (this.overflowUnitPrice) {
      this.overflowUnitPrice.textContent = `${formatMoney(unitCents, this.moneyFormat, this.currency)} each`;
    }
    if (this.overflowDiscountPill) {
      this.overflowDiscountPill.hidden = discountPercent <= 0;
      this.overflowDiscountPill.textContent = discountPercent > 0 ? `${discountPercent}% off` : '';
    }

    let repeatableValueCents = 0;
    for (const item of this.repeatableItems) {
      if (!item.unlockQuantity) continue;
      const repeatCount = Math.floor(qty / item.unlockQuantity);
      if (repeatCount > 0) repeatableValueCents += item.priceCents * repeatCount;
    }

    for (const badge of this.multiplierBadges) {
      const unlockQuantity = Number(badge.dataset.unlockQuantity) || 0;
      const repeatCount = unlockQuantity > 0 ? Math.floor(qty / unlockQuantity) : 0;
      badge.hidden = repeatCount <= 1;
      badge.textContent = repeatCount > 1 ? `×${repeatCount}` : '';
    }

    for (const priceEl of this.repeatablePriceEls) {
      const unlockQuantity = Number(priceEl.dataset.unlockQuantity) || 0;
      const unitPriceCents = Number(priceEl.dataset.unitPriceCents) || 0;
      const repeatCount = unlockQuantity > 0 ? Math.floor(qty / unlockQuantity) : 0;
      priceEl.hidden = repeatCount <= 0;
      if (repeatCount > 0) {
        priceEl.textContent = formatMoney(unitPriceCents * repeatCount, this.moneyFormat, this.currency);
      }
    }

    const totalBundleValueCents = subtotalCents + this.overflowFixedBonusValueCents + repeatableValueCents;
    const savingsCents = Math.max(0, totalBundleValueCents - bundleCents);
    if (this.overflowTotals) this.overflowTotals.hidden = savingsCents <= 0;
    if (this.overflowTotalsValue) {
      this.overflowTotalsValue.textContent =
        `${this.totalValueLabel} ${formatMoney(totalBundleValueCents, this.moneyFormat, this.currency)}`.trim();
    }
    if (this.overflowTotalsSavings) {
      this.overflowTotalsSavings.textContent =
        `${this.savingsLabel} ${formatMoney(savingsCents, this.moneyFormat, this.currency)}`.trim();
    }

    if (this.ctaLabel) {
      this.ctaLabel.textContent = `${this.ctaLabelBase} – ${formatMoney(bundleCents, this.moneyFormat, this.currency)}`;
    }
  }

  #restoreLastTierOriginal() {
    if (this.overflowTitle) this.overflowTitle.textContent = this.lastTierOriginal.title;
    if (this.overflowPrice) this.overflowPrice.textContent = this.lastTierOriginal.price;
    if (this.overflowUnitPrice) this.overflowUnitPrice.textContent = this.lastTierOriginal.unitPrice;
    if (this.overflowDiscountPill) {
      this.overflowDiscountPill.textContent = this.lastTierOriginal.pillText;
      this.overflowDiscountPill.hidden = this.lastTierOriginal.pillHidden;
    }
    if (this.overflowTotals) this.overflowTotals.hidden = this.lastTierOriginal.totalsHidden;
    if (this.overflowTotalsValue) this.overflowTotalsValue.textContent = this.lastTierOriginal.totalsValueText;
    if (this.overflowTotalsSavings) this.overflowTotalsSavings.textContent = this.lastTierOriginal.totalsSavingsText;
    this.multiplierBadges.forEach((badge, i) => {
      const original = this.lastTierOriginal.multipliers[i];
      if (!original) return;
      badge.textContent = original.text;
      badge.hidden = original.hidden;
    });
    this.repeatablePriceEls.forEach((priceEl, i) => {
      const original = this.lastTierOriginal.repeatablePrices[i];
      if (!original) return;
      priceEl.textContent = original.text;
      priceEl.hidden = original.hidden;
    });
  }

  /**
   * @param {Element} container
   * @param {number} quantity
   */
  #setActivePanel(container, quantity) {
    for (const el of container.children) {
      if (!(el instanceof HTMLElement)) continue;
      el.classList.toggle('is-active', Number(el.dataset.quantity) === quantity);
    }
  }
}

if (!customElements.get('bundle-quantity-selector-component')) {
  customElements.define('bundle-quantity-selector-component', BundleQuantitySelectorComponent);
}
